import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import Container from '@components/Container';
import { fetchBlogPosts, type BlogPost } from '@lib/api';

function formatDate(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function BlogHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role } = useUserRole(user?.id ?? null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchBlogPosts(false);
        if (!active) return;
        setPosts(data);
      } catch (err) {
        if (!active) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load stories at the moment.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  const sortedPosts = useMemo(() => {
    return [...posts].sort(
      (a, b) =>
        new Date(b.published_at ?? b.created_at).getTime() -
        new Date(a.published_at ?? a.created_at).getTime()
    );
  }, [posts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50">
      <Container className="py-16 lg:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm uppercase tracking-[0.4em] text-indigo-500">
            Xhalin Stories
          </p>
          <h1 className="mt-4 text-4xl font-bold text-gray-900 md:text-5xl">
            Breathwork insights crafted for the community.
          </h1>
          <p className="mt-5 text-base text-gray-600">
            Browse the latest guides, research-backed reflections, and personal
            stories we publish to help you breathe better every day.
          </p>
        </div>

        {role === 'admin' ? (
          <div className="mt-8 flex justify-center lg:justify-end">
            <Button
              type="button"
              onClick={() => navigate('/admin/blog/new')}
              className="!rounded-lg !bg-indigo-600 !px-5 !py-2.5 !text-white shadow-lg shadow-indigo-200 hover:!bg-indigo-500 focus:!ring-indigo-400"
            >
              + Create Post
            </Button>
          </div>
        ) : null}

        <div className="mt-12 space-y-6">
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((skeleton) => (
                <div
                  key={skeleton}
                  className="h-60 animate-pulse rounded-3xl bg-white/70 shadow-lg"
                />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 px-6 py-4 text-sm text-rose-700">
              {error}
            </div>
          ) : sortedPosts.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-indigo-200 bg-white/80 px-6 py-10 text-center text-sm text-gray-500">
              No stories published yet. Check back soon for new posts.
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {sortedPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block"
                  aria-label={`Read ${post.title}`}
                >
                  <article className="flex min-h-[360px] flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg transition-shadow duration-150 hover:shadow-xl focus-visible:outline focus-visible:ring focus-visible:ring-indigo-300">
                    {post.cover_image ? (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="h-44 w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-44 bg-gradient-to-r from-indigo-200 via-purple-200 to-emerald-200" />
                    )}
                    <div className="flex flex-1 flex-col gap-4 p-5">
                      <div>
                        <p className="text-xs uppercase tracking-[0.4em] text-indigo-500">
                          {formatDate(post.published_at) ?? 'Draft'}
                        </p>
                        <h2 className="mt-2 text-lg font-semibold text-gray-900">
                          {post.title}
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                          {post.excerpt || post.content.slice(0, 140)}
                        </p>
                      </div>
                      <div className="mt-auto">
                        <span className="text-sm font-semibold text-indigo-600 transition-colors duration-150 group-hover:text-indigo-500">
                          Continue reading &rarr;
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}

export default BlogHome;
